import ModalComponent from 'components/Common/Modal';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import React, { useContext } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

const PriorityBadge = (props) => {
  const { item, numberOfContributions, feedItemFlags } = props ?? {};
  const { type, sourceOrganisation, source, house } = item ?? {};
  const { matchingKeywordLists, sourceScore, noveltyScore, eventCountScore } = feedItemFlags ?? {};
  const { setPriorityBadgeModalState } = usePriorityBadgeState();
  const isMobile = useIsMobile(640);

  //OP: HARDCODED VALUES FOR NOW
  const itemData = {
    type: type,
    sourceScore: sourceScore,
    noveltyScore: noveltyScore,
    eventCountScore: eventCountScore,
    relevantContributions: numberOfContributions,
    matchingKeywordLists: matchingKeywordLists?.length,
    sourceOrganisation,
    source,
    house,
  };

  return (
    <button
      className='general-button mb-0 ml-2 specific-right-margin-5 specific-right-padding-12 text-light-blue nowrap-item paragraph-p2 d-flex align-items-center transition-100 border-right hover-opacity-85'
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setPriorityBadgeModalState({ itemData, isOpen: true });
      }}
    >
      <span>Priority{isMobile ? '' : ' update'}</span>{' '}
    </button>
  );
};

const PriorityBadgeModal = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const teamName = (activePlan ?? {}).teamName ?? 'your organisation';
  const { newAgencyPlan } = useTopicOrClientWord();
  const { priorityBadgeModalState, setPriorityBadgeModalState } = usePriorityBadgeState();
  const { itemData, isOpen } = priorityBadgeModalState ?? {};
  const {
    type,
    sourceScore,
    noveltyScore,
    eventCountScore,
    relevantContributions,
    matchingKeywordLists,
    sourceOrganisation,
    source,
    house,
  } = itemData ?? {};

  const handleClose = () => {
    setPriorityBadgeModalState({ ...priorityBadgeModalState, isOpen: false });
  };

  const getTextMapping = () => {
    const typeOfResourceMapping = {
      KeyUpdate: <>This update contains news from an established source</>,
      Consultation: (
        <>
          This update is a <span className='font-weight-semibold text-main-blue-80'>consultation</span>, which means it
          may be <span className='font-weight-semibold text-main-blue-80'>time-sensitive</span>
        </>
      ),
      Legislation: (
        <>
          This update relates to <span className='font-weight-semibold text-main-blue-80'>legislation</span> on which{' '}
          {teamName} may want to have input
        </>
      ),
      ParliamentaryRecord: (
        <>
          This update is from the <span className='font-weight-semibold text-main-blue-80'>parliamentary record</span>,
          meaning it reflects official{' '}
          <span className='font-weight-semibold text-main-blue-80'>statements, questions or debates</span>
        </>
      ),
      CommitteePublication: (
        <>
          This update is a <span className='font-weight-semibold text-main-blue-80'>committee publication</span> which
          may include scrutiny of issues of importance to {teamName}
        </>
      ),
      LibraryArticle: (
        <>
          This update is from the{' '}
          <span className='font-weight-semibold text-main-blue-80'>House of {house ?? 'Commons'} Library</span> and may
          include authoritative background, evidence or context
        </>
      ),
      UserContent: (
        <>
          This update is <span className='font-weight-semibold text-main-blue-80'>lobbying material</span> added to
          PolicyMogul by an organisation, and may offer an opportunity for collaboration
        </>
      ),
      Tweet: 'This is an X post, and may indicate a position that isn’t yet reflected in official channels',
    };

    return [
      {
        title: 'Type of resource',
        description: typeOfResourceMapping[type],
        show: !!typeOfResourceMapping[type],
      },
      {
        title: 'Contributor',
        description: (
          <>
            The contributor (
            {source ? (
              <a
                className={`link-button text-hover-light-blue pointer`}
                href={source}
                target='_blank'
                rel='noopener noreferrer'
              >
                {sourceOrganisation}
              </a>
            ) : (
              <span className='font-weight-semibold text-main-blue-80'>{sourceOrganisation}</span>
            )}
            ) has been deemed important based on their relevant positions and memberships
          </>
        ),
        show: sourceScore > 6 && sourceOrganisation && type !== 'KeyUpdate',
      },
      {
        title: 'Salience',
        description: `This update was deemed potentially significant to ${teamName}, based on ${newAgencyPlan ? 'their' : 'your'} description and areas of interest`,
        show: true,
      },
      {
        title: 'Novelty',
        description:
          'This update is deemed novel because it introduces information or activity not seen in the last 30 days',
        show: noveltyScore > 0.8,
      },
      {
        title: 'Contributions matching your keywords',
        description: `This update contains ${relevantContributions} relevant contribution${
          relevantContributions > 1 ? 's' : ''
        } from ${matchingKeywordLists} topic${matchingKeywordLists > 1 ? 's' : ''}`,
        show: eventCountScore > 0.75 && relevantContributions > 1 && matchingKeywordLists > 0,
      },
    ];
  };

  const textMapping = getTextMapping();
  const filteredTextMapping = textMapping.filter((item) => item.show);

  return (
    <ModalComponent
      isOpen={isOpen}
      setIsOpen={handleClose}
      overlayClassName='react-modal-overlay adjustment-of-position'
      maxWidth={560}
      onAfterClose={() => setPriorityBadgeModalState({ ...priorityBadgeModalState, itemData: null })}
    >
      <h3 className='topic-title topic-title-email-alerts mb-0 section-title pt-4 pb-3 px-lg-5 px-3'>
        Why has this been identified as a priority update?
      </h3>
      <div className='py-4 specific-x-padding-40' style={{ maxHeight: 'calc(90vh - 73px)', overflowY: 'auto' }}>
        {filteredTextMapping.map((item, index) => (
          <div
            key={normalize(item.title)}
            className={`${index === filteredTextMapping.length - 1 ? '' : 'specific-bottom-margin-24'}`}
          >
            <h3 className='title-h5-bold text-main-blue'>{item.title}</h3>
            <p className='paragraph-p1 text-main-blue-60'>{item.description}</p>
          </div>
        ))}
      </div>
    </ModalComponent>
  );
};

const usePriorityBadgeState = () => {
  const { priorityBadgeModalState, setFeedState } = useContentResults();
  const setPriorityBadgeModalState = (value) => {
    setFeedState('priorityBadgeModalState', value);
  };
  return { priorityBadgeModalState, setPriorityBadgeModalState };
};

export { PriorityBadgeModal, usePriorityBadgeState };
export default PriorityBadge;
